/**
 * VendorPortalContactService
 * Service for managing vendor contacts through the vendor portal.
 * Route: api/vendor-portal/contacts
 *
 * Vendors can:
 * - View all their contacts
 * - Create new contacts
 * - Update existing contacts (with restrictions)
 * - Delete contacts (with restrictions)
 *
 * Restrictions:
 * - Cannot change isPrimary after vendor submission (status != DRAFT)
 * - Cannot delete the only active contact
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalContactService
 * Handles vendor contact management for the vendor portal.
 * Maps to /api/vendor-portal/contacts endpoint
 */
export class VendorPortalContactService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalContactService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalContactService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all contacts for the current vendor
     * GET /api/vendor-portal/contacts
     */
    async getContacts() {
        const response = await this.getContactsRaw();
        return response.data ?? [];
    }
    /**
     * Get all contacts (raw response)
     */
    getContactsRaw() {
        return this.get();
    }
    // ==================== GET BY ID ====================
    /**
     * Get a specific contact by ID
     * GET /api/vendor-portal/contacts/{id}
     * Server validates ownership before returning
     */
    async getContact(id) {
        const response = await this.getContactRaw(id);
        if (!response.data) {
            throw new Error(`Contact with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get a specific contact (raw response)
     */
    getContactRaw(id) {
        return this.get(String(id));
    }
    // ==================== CREATE ====================
    /**
     * Create a new contact for the current vendor
     * POST /api/vendor-portal/contacts
     */
    async createContact(request) {
        const response = await this.createContactRaw(request);
        if (!response.data) {
            throw new Error('Failed to create contact');
        }
        return response.data;
    }
    /**
     * Create a new contact (raw response)
     */
    createContactRaw(request) {
        return this.post('', request);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing contact
     * PUT /api/vendor-portal/contacts/{id}
     * Server validates ownership before updating
     *
     * Note: Cannot modify isPrimary after vendor submission
     */
    async updateContact(id, request) {
        const response = await this.updateContactRaw(id, request);
        if (!response.data) {
            throw new Error(`Failed to update contact ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing contact (raw response)
     */
    updateContactRaw(id, request) {
        return this.put(String(id), request);
    }
    // ==================== DELETE ====================
    /**
     * Delete a contact
     * DELETE /api/vendor-portal/contacts/{id}
     * Server validates ownership before deleting
     *
     * Note: Cannot delete the only active contact
     */
    async deleteContact(id) {
        await this.deleteContactRaw(id);
    }
    /**
     * Delete a contact (raw response)
     */
    deleteContactRaw(id) {
        return this.delete(String(id));
    }
}
VendorPortalContactService.DEFAULT_SERVICE_PATH = 'vendors';
VendorPortalContactService.BASE_PATH = 'api/vendor-portal/contacts';
//# sourceMappingURL=VendorPortalContactService.js.map